/**
 * FERP Portal - Reconciliation Dashboard (DRR-221 Phase 5)
 *
 * Cross-platform data reconciliation monitoring:
 * - Time tracking vs Payroll variance detection
 * - Employee roster mismatches
 * - Fraud indicators (paid not worked)
 *
 * Data sources:
 * - Analytics primitive (type: RECONCILIATION)
 * - Notification primitive (variance alerts)
 */

import { useState } from 'react';
import {
  Container,
  Paper,
  Stack,
  Title,
  Text,
  Badge,
  Group,
  Table,
  Tabs,
  SimpleGrid,
  Card,
  ThemeIcon,
  Progress,
  Alert,
  Anchor,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconClock,
  IconUsers,
  IconArrowsExchange,
  IconListDetails,
  IconChartBar,
} from '@tabler/icons-react';
import { useTenantId } from '@/auth/AuthContext';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

// GraphQL query for reconciliation analytics
const GET_RECONCILIATION_REPORTS = gql`
  query GetReconciliationReports($tenantId: ID!, $limit: Int) {
    analytics(
      tenantId: $tenantId
      filter: { analyticsType: RECONCILIATION }
      limit: $limit
    ) {
      id
      name
      description
      periodStart
      periodEnd
      currentValue
      previousValue
      metadata
      createdAt
    }
  }
`;

interface VarianceSummary {
  type: string;
  count: number;
  severity: 'critical' | 'high' | 'medium' | 'low';
  color: string;
}

interface ReconciliationReport {
  id: string;
  name: string;
  periodStart: string;
  periodEnd: string;
  employeesMatched: number;
  employeesUnmatched: number;
  totalVarianceHours: number;
  criticalVariances: number;
  highVariances: number;
  createdAt: string;
}

// Variance type labels
const VARIANCE_LABELS: Record<string, string> = {
  WORKED_NOT_PAID: 'Worked but Not Paid',
  PAID_NOT_WORKED: 'Paid but Not Worked',
  HOURS_VARIANCE: 'Hours Mismatch',
  ROSTER_MISMATCH_RC: 'Missing from Payroll',
  ROSTER_MISMATCH_PR: 'Missing from Time Tracking',
  OVERTIME_VARIANCE: 'Overtime Mismatch',
};

function SummaryCard({
  title,
  value,
  icon,
  color,
  subtitle,
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}) {
  return (
    <Card withBorder radius="md" padding="lg">
      <Group justify="space-between" align="flex-start">
        <div>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
            {title}
          </Text>
          <Text size="xl" fw={700} mt="xs">
            {value}
          </Text>
          {subtitle && (
            <Text size="xs" c="dimmed" mt={4}>
              {subtitle}
            </Text>
          )}
        </div>
        <ThemeIcon size="xl" radius="md" variant="light" color={color}>
          {icon}
        </ThemeIcon>
      </Group>
    </Card>
  );
}

function VarianceTable({ variances }: { variances: VarianceSummary[] }) {
  const rows = variances.map((v) => (
    <Table.Tr key={v.type}>
      <Table.Td>
        <Badge color={v.color} variant="light">
          {v.severity.toUpperCase()}
        </Badge>
      </Table.Td>
      <Table.Td>{VARIANCE_LABELS[v.type] || v.type}</Table.Td>
      <Table.Td ta="right">
        <Text fw={600}>{v.count}</Text>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Table>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Severity</Table.Th>
          <Table.Th>Variance Type</Table.Th>
          <Table.Th ta="right">Count</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>{rows}</Table.Tbody>
    </Table>
  );
}

function ReportHistory({ reports }: { reports: ReconciliationReport[] }) {
  const rows = reports.map((r) => (
    <Table.Tr key={r.id}>
      <Table.Td>
        <Text size="sm">{new Date(r.createdAt).toLocaleDateString()}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">
          {r.periodStart} - {r.periodEnd}
        </Text>
      </Table.Td>
      <Table.Td ta="right">
        <Text size="sm">{r.employeesMatched}</Text>
      </Table.Td>
      <Table.Td ta="right">
        <Badge color={r.criticalVariances > 0 ? 'red' : 'green'} variant="light">
          {r.criticalVariances}
        </Badge>
      </Table.Td>
      <Table.Td ta="right">
        <Badge color={r.highVariances > 0 ? 'orange' : 'green'} variant="light">
          {r.highVariances}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Anchor size="sm" href={`/reconciliation/report/${r.id}`}>
          View
        </Anchor>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Table>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Run Date</Table.Th>
          <Table.Th>Period</Table.Th>
          <Table.Th ta="right">Matched</Table.Th>
          <Table.Th ta="right">Critical</Table.Th>
          <Table.Th ta="right">High</Table.Th>
          <Table.Th>Actions</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>{rows}</Table.Tbody>
    </Table>
  );
}

export function ReconciliationDashboardPage() {
  const tenantId = useTenantId();
  const [activeTab, setActiveTab] = useState<string | null>('overview');

  // Query reconciliation reports (uses mock data until backend wired)
  const { data: _reportsData } = useQuery(GET_RECONCILIATION_REPORTS, {
    variables: { tenantId, limit: 10 },
    skip: !tenantId,
  });

  // Mock data for demo (replace with real data from queries)
  const mockSummary = {
    totalEmployees: 772,
    matchedEmployees: 770,
    totalHoursRC: 243375,
    totalHoursPR: 243375,
    hoursVariance: 12.5,
    lastRunTime: '2026-04-09 06:30 AM',
    nextRunTime: '2026-04-10 06:30 AM',
  };

  const mockVariances: VarianceSummary[] = [
    { type: 'PAID_NOT_WORKED', count: 2, severity: 'critical', color: 'red' },
    { type: 'WORKED_NOT_PAID', count: 3, severity: 'critical', color: 'red' },
    { type: 'ROSTER_MISMATCH_PR', count: 3, severity: 'high', color: 'orange' },
    { type: 'HOURS_VARIANCE', count: 8, severity: 'high', color: 'orange' },
    { type: 'ROSTER_MISMATCH_RC', count: 2, severity: 'medium', color: 'yellow' },
    { type: 'OVERTIME_VARIANCE', count: 12, severity: 'medium', color: 'yellow' },
  ];

  const mockReports: ReconciliationReport[] = [
    {
      id: 'rcn-001',
      name: 'Time-Payroll Reconciliation',
      periodStart: '2026-04-01',
      periodEnd: '2026-04-08',
      employeesMatched: 770,
      employeesUnmatched: 5,
      totalVarianceHours: 12.5,
      criticalVariances: 5,
      highVariances: 11,
      createdAt: '2026-04-09T06:30:00Z',
    },
    {
      id: 'rcn-002',
      name: 'Time-Payroll Reconciliation',
      periodStart: '2026-03-25',
      periodEnd: '2026-03-31',
      employeesMatched: 768,
      employeesUnmatched: 4,
      totalVarianceHours: 8.25,
      criticalVariances: 2,
      highVariances: 7,
      createdAt: '2026-04-02T06:30:00Z',
    },
  ];

  const criticalCount = mockVariances
    .filter((v) => v.severity === 'critical')
    .reduce((sum, v) => sum + v.count, 0);
  const highCount = mockVariances
    .filter((v) => v.severity === 'high')
    .reduce((sum, v) => sum + v.count, 0);

  const matchRate = Math.round(
    (mockSummary.matchedEmployees / mockSummary.totalEmployees) * 100
  );

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Reconciliation Dashboard</Title>
            <Text c="dimmed" size="sm">
              Cross-platform data reconciliation: Time Tracking vs Payroll
            </Text>
          </div>
          <Group gap="xs">
            <Badge leftSection={<IconClock size={12} />} variant="light">
              Last run: {mockSummary.lastRunTime}
            </Badge>
            <Badge leftSection={<IconClock size={12} />} variant="outline">
              Next: {mockSummary.nextRunTime}
            </Badge>
          </Group>
        </Group>

        {/* Critical Alert Banner */}
        {criticalCount > 0 && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            title="Critical Variances Detected"
            color="red"
            variant="light"
          >
            {criticalCount} critical variance(s) require immediate investigation.
            These may indicate fraud or significant process failures.
          </Alert>
        )}

        {/* Summary Cards */}
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
          <SummaryCard
            title="Employee Match Rate"
            value={`${matchRate}%`}
            icon={<IconUsers size={24} />}
            color="green"
            subtitle={`${mockSummary.matchedEmployees} of ${mockSummary.totalEmployees} matched`}
          />
          <SummaryCard
            title="Hours Variance"
            value={`${mockSummary.hoursVariance}h`}
            icon={<IconClock size={24} />}
            color={mockSummary.hoursVariance > 10 ? 'orange' : 'green'}
            subtitle="Total hours difference"
          />
          <SummaryCard
            title="Critical Issues"
            value={criticalCount}
            icon={<IconAlertTriangle size={24} />}
            color={criticalCount > 0 ? 'red' : 'green'}
            subtitle="Fraud indicators"
          />
          <SummaryCard
            title="High Priority"
            value={highCount}
            icon={<IconArrowsExchange size={24} />}
            color={highCount > 5 ? 'orange' : 'blue'}
            subtitle="Require attention"
          />
        </SimpleGrid>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconChartBar size={16} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab
              value="variances"
              leftSection={<IconAlertTriangle size={16} />}
              rightSection={
                <Badge size="xs" color="red" variant="filled">
                  {criticalCount + highCount}
                </Badge>
              }
            >
              Variances
            </Tabs.Tab>
            <Tabs.Tab value="history" leftSection={<IconListDetails size={16} />}>
              History
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="md">
            <SimpleGrid cols={{ base: 1, md: 2 }}>
              {/* Variance Summary */}
              <Paper p="md" withBorder radius="md">
                <Title order={4} mb="md">
                  Variance Summary
                </Title>
                <VarianceTable variances={mockVariances} />
              </Paper>

              {/* Platform Comparison */}
              <Paper p="md" withBorder radius="md">
                <Title order={4} mb="md">
                  Platform Comparison
                </Title>
                <Stack gap="md">
                  <div>
                    <Group justify="space-between" mb={4}>
                      <Text size="sm">RevivingCare Hours</Text>
                      <Text size="sm" fw={600}>
                        {mockSummary.totalHoursRC.toLocaleString()}
                      </Text>
                    </Group>
                    <Progress value={100} color="blue" size="lg" />
                  </div>
                  <div>
                    <Group justify="space-between" mb={4}>
                      <Text size="sm">Payroll Relief Hours</Text>
                      <Text size="sm" fw={600}>
                        {mockSummary.totalHoursPR.toLocaleString()}
                      </Text>
                    </Group>
                    <Progress
                      value={(mockSummary.totalHoursPR / mockSummary.totalHoursRC) * 100}
                      color="green"
                      size="lg"
                    />
                  </div>
                  <Text size="xs" c="dimmed" ta="center">
                    {mockSummary.hoursVariance}h variance ({((mockSummary.hoursVariance / mockSummary.totalHoursRC) * 100).toFixed(3)}%)
                  </Text>
                </Stack>
              </Paper>
            </SimpleGrid>
          </Tabs.Panel>

          <Tabs.Panel value="variances" pt="md">
            <Paper p="md" withBorder radius="md">
              <Title order={4} mb="md">
                All Variances
              </Title>
              <Text c="dimmed" size="sm" mb="md">
                Detailed variance list will be loaded from backend queries.
                For now, showing summary by type.
              </Text>
              <VarianceTable variances={mockVariances} />
            </Paper>
          </Tabs.Panel>

          <Tabs.Panel value="history" pt="md">
            <Paper p="md" withBorder radius="md">
              <Title order={4} mb="md">
                Reconciliation History
              </Title>
              <ReportHistory reports={mockReports} />
            </Paper>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}

export default ReconciliationDashboardPage;
